import React, { useCallback, useEffect, useState } from 'react';
import { useBackRoute } from '../hooks/useBackRoute';
import { parseApiDate } from '../utils/security';
import {
  createTsKayitDavet,
  fetchTsKayitBasvurulari,
  fetchTsKayitBasvuruDetay,
  fetchTsKayitBasvuruSekmeSayilari,
  onaylaTsKayitBasvuru,
  pasifAlTsKayitDavet,
  reddetTsKayitBasvuru,
  TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT,
} from '../services/tsKayitBasvuruService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import TsKayitBasvuruDetayModal from '../components/TsKayitBasvuruDetayModal';
import TsKayitDavetGecerlilikModal from '../components/TsKayitDavetGecerlilikModal';
import TsKayitDavetLinkCard from '../components/TsKayitDavetLinkCard';
import TsKayitNotlarPanel from '../components/TsKayitNotlarPanel';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';
import '../components/BayilikBasvuruDetayModal.css';

const SEKMELER = [
  { id: 'onay_bekliyor', label: 'Onay Bekliyor' },
  { id: 'onaylandi', label: 'Onaylandı' },
  { id: 'reddedildi', label: 'Reddedildi' },
  { id: 'davetler', label: 'Davet Linkleri' },
  { id: 'notlar', label: 'Notlar' },
];

const DURUM_LABELS = {
  onay_bekliyor: 'Onay Bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
};

const DURUM_BADGE = {
  onay_bekliyor: { bg: '#fef3c7', color: '#92400e' },
  onaylandi: { bg: '#d1fae5', color: '#065f46' },
  reddedildi: { bg: '#fee2e2', color: '#991b1b' },
};

const DAVET_DURUM_LABELS = {
  aktif: 'Aktif',
  suresi_doldu: 'Süresi doldu',
  pasif: 'Pasif',
};

const DAVET_DURUM_BADGE = {
  aktif: { bg: '#d1fae5', color: '#065f46' },
  suresi_doldu: { bg: '#f1f5f9', color: '#475569' },
  pasif: { bg: '#fef3c7', color: '#92400e' },
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

function DurumBadge({ durum }) {
  const style = DURUM_BADGE[durum] || { bg: '#f1f5f9', color: '#334155' };
  return (
    <span className="bo-badge" style={{ backgroundColor: style.bg, color: style.color }}>
      {DURUM_LABELS[durum] || durum}
    </span>
  );
}

function DavetDurumBadge({ durum }) {
  const style = DAVET_DURUM_BADGE[durum] || { bg: '#f1f5f9', color: '#334155' };
  return (
    <span className="bo-badge" style={{ backgroundColor: style.bg, color: style.color }}>
      {DAVET_DURUM_LABELS[durum] || durum}
    </span>
  );
}

const TsKayitOnaylariPage = () => {
  const { confirm } = useConfirmDialog();
  const goBack = useBackRoute('/');
  const [sekme, setSekme] = useState('onay_bekliyor');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pasifLoadingId, setPasifLoadingId] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [sekmeSayilari, setSekmeSayilari] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [redNedeni, setRedNedeni] = useState('');
  const [gecerlilikDavet, setGecerlilikDavet] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const refreshSekmeSayilari = useCallback(async () => {
    try {
      const res = await fetchTsKayitBasvuruSekmeSayilari();
      if (res?.basarili && res?.veri) setSekmeSayilari(res.veri);
    } catch {
      setSekmeSayilari({});
    }
  }, []);

  const loadItems = useCallback(async () => {
    if (sekme === 'notlar') {
      setItems([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetchTsKayitBasvurulari({
        sekme,
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
      });
      if (res?.basarili === false) {
        setError(res.mesaj || 'Liste yüklenemedi');
        setItems([]);
        return;
      }
      setItems(res?.veri?.items || []);
      setPaginationMeta(normalizeSayfalama(res?.veri?.sayfalama));
    } catch (err) {
      setError(getErrorMessage(err, 'Liste yüklenemedi'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sekme, currentPage]);

  useEffect(() => {
    loadItems();
    refreshSekmeSayilari();
  }, [loadItems, refreshSekmeSayilari]);

  useEffect(() => {
    const onRefresh = () => {
      loadItems();
      refreshSekmeSayilari();
    };
    window.addEventListener(TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
    return () => window.removeEventListener(TS_KAYIT_BASVURU_SAYAC_YENILE_EVENT, onRefresh);
  }, [loadItems, refreshSekmeSayilari]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sekme]);

  const handleCreateLink = async () => {
    setLinkLoading(true);
    setCreatedLink('');
    try {
      const res = await createTsKayitDavet();
      if (res?.basarili === false) {
        setError(res.mesaj || 'Link oluşturulamadı');
        return;
      }
      const url = res?.veri?.url || '';
      setCreatedLink(url);
      window.showNotification?.('success', 'Link oluşturuldu');
      if (sekme === 'davetler') loadItems();
    } catch (err) {
      setError(getErrorMessage(err, 'Link oluşturulamadı'));
    } finally {
      setLinkLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedItem(null);
    setDetailError('');
    setRedNedeni('');
  };

  const openDetail = async (item) => {
    setDetailOpen(true);
    setDetailError('');
    setRedNedeni('');

    if (sekme === 'davetler') {
      setSelectedItem(item);
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    setSelectedItem(item);
    try {
      const res = await fetchTsKayitBasvuruDetay(item.id);
      if (res?.basarili === false) {
        setDetailError(res.mesaj || 'Detay yüklenemedi');
        return;
      }
      setSelectedItem(res?.veri || item);
    } catch (err) {
      setDetailError(getErrorMessage(err, 'Detay yüklenemedi'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOnayla = async () => {
    if (!selectedItem?.id) return;
    const ok = await confirm({
      title: 'Başvuruyu onayla',
      message: 'Teknik servis panel hesabı oluşturulacak.',
      confirmLabel: 'Onayla',
      variant: 'info',
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      const res = await onaylaTsKayitBasvuru(selectedItem.id);
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Onaylanamadı');
        return;
      }
      window.showNotification?.('success', 'Başvuru onaylandı');
      closeDetail();
      loadItems();
      refreshSekmeSayilari();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Onaylanamadı'));
    } finally {
      setActionLoading(false);
    }
  };

  const openGecerlilikDuzenle = (item, { closeDetailModal = false } = {}) => {
    if (!item?.id || item.durum === 'pasif') return;
    if (closeDetailModal) closeDetail();
    setGecerlilikDavet(item);
  };

  const handleGecerlilikSaved = (updated) => {
    if (!updated?.id) return;
    setItems((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
    if (selectedItem?.id === updated.id) {
      setSelectedItem((prev) => (prev ? { ...prev, ...updated } : prev));
    }
  };

  const handlePasifAl = async (item, { closeModal = false } = {}) => {
    if (!item?.id || item.durum !== 'aktif') return;
    const ok = await confirm({
      title: 'Linki pasife al',
      message: 'Davet linki kapatılacak.',
      confirmLabel: 'Pasife al',
      variant: 'danger',
    });
    if (!ok) return;

    setPasifLoadingId(item.id);
    try {
      const res = await pasifAlTsKayitDavet(item.id);
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Pasife alınamadı');
        return;
      }
      window.showNotification?.('success', 'Link pasife alındı');
      if (closeModal) closeDetail();
      loadItems();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Pasife alınamadı'));
    } finally {
      setPasifLoadingId('');
    }
  };

  const handleReddet = async () => {
    if (!selectedItem?.id) return;
    const neden = redNedeni.trim();
    if (!neden) {
      window.showNotification?.('error', 'Red nedeni gereklidir');
      return;
    }
    const ok = await confirm({
      title: 'Başvuruyu reddet',
      message: 'Başvuru reddedilecek.',
      confirmLabel: 'Reddet',
      variant: 'danger',
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      const res = await reddetTsKayitBasvuru(selectedItem.id, neden);
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Reddedilemedi');
        return;
      }
      window.showNotification?.('success', 'Başvuru reddedildi');
      closeDetail();
      loadItems();
      refreshSekmeSayilari();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Reddedilemedi'));
    } finally {
      setActionLoading(false);
    }
  };

  const isDavetSekme = sekme === 'davetler';

  return (
    <main className="main-content bayilik-onaylari-page">
      <div className="content-header content-header-row">
        <div>
          <button type="button" className="secondary-btn" onClick={goBack} style={{ marginBottom: 16 }}>
            <i className="fas fa-arrow-left" />
            <span>Geri Dön</span>
          </button>
          <h1>Servis Onayları</h1>
        </div>
        <button
          type="button"
          className="primary-btn"
          onClick={handleCreateLink}
          disabled={linkLoading}
        >
          <i className="fas fa-link" />
          <span>{linkLoading ? 'Oluşturuluyor...' : 'Link Oluştur'}</span>
        </button>
      </div>

      {createdLink && <TsKayitDavetLinkCard url={createdLink} />}

      <div className="bo-tabs">
        {SEKMELER.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`status-tab-btn ${sekme === tab.id ? 'active' : ''}`}
            onClick={() => setSekme(tab.id)}
          >
            {tab.label}
            {tab.id !== 'davetler' && sekmeSayilari[tab.id] > 0 && (
              <span className="bo-tab-badge">{sekmeSayilari[tab.id]}</span>
            )}
          </button>
        ))}
      </div>

      {sekme === 'notlar' ? (
        <TsKayitNotlarPanel onChange={refreshSekmeSayilari} />
      ) : (
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : items.length === 0 ? (
          <div className="empty-state" style={{ padding: 40, textAlign: 'center' }}>
            <i className="fas fa-tools" style={{ fontSize: 32, color: 'var(--text-light)', marginBottom: 12 }} />
            <div style={{ color: 'var(--text-gray)', fontSize: 14 }}>Kayıt yok</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  {isDavetSekme ? (
                    <tr>
                      <th>Durum</th>
                      <th>Geçerlilik</th>
                      <th>Oluşturma</th>
                      <th>Link</th>
                      <th className="islemler-sutunu">İşlem</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Tarih</th>
                      <th>Teknik Servis</th>
                      <th>Kullanıcı Adı</th>
                      <th>Durum</th>
                      <th className="islemler-sutunu">İşlem</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {isDavetSekme
                    ? items.map((item) => (
                      <tr
                        key={item.id}
                        className="bo-clickable"
                        onClick={() => openDetail(item)}
                      >
                        <td><DavetDurumBadge durum={item.durum} /></td>
                        <td>{formatTarih(item.gecerlilikBitis)}</td>
                        <td>{formatTarih(item.createdAt)}</td>
                        <td className="bo-url-cell" title={item.url}>
                          <span className={item.durum === 'pasif' ? 'bo-url-muted' : undefined}>{item.url}</span>
                        </td>
                        <td className="islemler-sutunu" onClick={(e) => e.stopPropagation()}>
                          <div className="table-actions">
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => openDetail(item)}
                              title="Detay"
                            >
                              <i className="fas fa-eye" />
                            </button>
                            {item.durum !== 'pasif' && (
                              <button
                                type="button"
                                className="category-action-btn edit-btn"
                                onClick={() => openGecerlilikDuzenle(item)}
                                title="Geçerlilik düzenle"
                              >
                                <i className="fas fa-pen" />
                              </button>
                            )}
                            {item.durum === 'aktif' && (
                              <button
                                type="button"
                                className="category-action-btn delete-btn"
                                onClick={() => handlePasifAl(item)}
                                title="Pasife al"
                                disabled={pasifLoadingId === item.id}
                              >
                                <i className="fas fa-ban" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                    : items.map((item) => (
                      <tr
                        key={item.id}
                        className="bo-clickable"
                        onClick={() => openDetail(item)}
                      >
                        <td>{formatTarih(item.createdAt)}</td>
                        <td className="bo-dealer-cell">
                          <strong>{item.techService?.ad || '—'}</strong>
                          {item.techService?.kod != null ? <small>#{item.techService.kod}</small> : null}
                        </td>
                        <td>{item.kullaniciAdi}</td>
                        <td><DurumBadge durum={item.durum} /></td>
                        <td className="islemler-sutunu" onClick={(e) => e.stopPropagation()}>
                          <div className="table-actions">
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => openDetail(item)}
                              title="Detay"
                            >
                              <i className="fas fa-eye" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>
      )}

      {detailOpen && (
        <TsKayitBasvuruDetayModal
          detail={!isDavetSekme ? selectedItem : null}
          davetDetail={isDavetSekme ? selectedItem : null}
          loading={detailLoading}
          error={detailError}
          onClose={closeDetail}
          onOnayla={handleOnayla}
          onReddet={handleReddet}
          redNedeni={redNedeni}
          onRedNedeniChange={setRedNedeni}
          actionLoading={actionLoading}
          pasifLoading={pasifLoadingId === selectedItem?.id}
          onPasifAl={() => handlePasifAl(selectedItem, { closeModal: true })}
          onGecerlilikDuzenle={() => openGecerlilikDuzenle(selectedItem, { closeDetailModal: true })}
          mode={isDavetSekme ? 'davet' : 'basvuru'}
        />
      )}

      {gecerlilikDavet && (
        <TsKayitDavetGecerlilikModal
          davet={gecerlilikDavet}
          onClose={() => setGecerlilikDavet(null)}
          onSaved={handleGecerlilikSaved}
        />
      )}
    </main>
  );
};

export default TsKayitOnaylariPage;
